// PRD 08 §3.1 — Disease Code add/edit slide-panel (520px). Required: code, name.
// Duplicate code (per group, per answer #5) surfaces from BE as CODE_DUPLICATE
// and pins to the `code` input. Cancel uses dirty-check + discard confirm.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { SlidePanel } from '@/components/feedback/SlidePanel';
import { toast } from '@/components/feedback/useToast';
import { hasEmoji } from '@/shared/utils/emoji';

import {
  useCreateDiseaseCode,
  useUpdateDiseaseCode,
} from '../hooks/use-disease-code-mutations';
import {
  DiseaseCodeApiError,
  type DiseaseCode,
  type DiseaseCodeStatus,
} from '../types/disease-code.types';
import { getDiseaseCodeErrorMessage } from '../utils/error-message';

interface DiseaseCodeFormPanelProps {
  open: boolean;
  // Required: must be the currently selected group's id (parent enforces).
  groupId: number;
  // Selected group's ICD-10 range — used to enforce PRD §8 "code must lie inside
  // the owning group's range". Empty strings skip the range check (group not loaded).
  rangeStart: string;
  rangeEnd: string;
  // Null = create mode; code = edit mode.
  code: DiseaseCode | null;
  onClose: () => void;
}

type FieldKey = 'code' | 'name' | 'description' | 'status';

interface FormState {
  code: string;
  name: string;
  description: string;
  status: DiseaseCodeStatus;
}

const EMPTY_FORM: FormState = {
  code: '',
  name: '',
  description: '',
  status: 'ACTIVE',
};

// FEATURE_031 disease-name charset (DH-DISEASE-CHARSET-001): 질병명 is Hangul + Latin letter (a-z A-Z)
// + digit + single space — a whitelist matching the BE @Pattern. Emoji, punctuation, and double-spaces
// are still rejected. Name fails when it does NOT match this pattern.
const NAME_CHARSET_RE = /^[가-힣ㄱ-ㅎㅏ-ㅣa-zA-Z0-9]+( [가-힣ㄱ-ㅎㅏ-ㅣa-zA-Z0-9]+)*$/;

function formFromCode(code: DiseaseCode | null): FormState {
  if (!code) return EMPTY_FORM;
  return {
    code: code.code,
    name: code.name,
    description: code.description ?? '',
    status: code.status,
  };
}

export function DiseaseCodeFormPanel({
  open,
  groupId,
  rangeStart,
  rangeEnd,
  code,
  onClose,
}: DiseaseCodeFormPanelProps) {
  const { t } = useTranslation('admin');
  const isEdit = code !== null;

  const createMutation = useCreateDiseaseCode();
  const updateMutation = useUpdateDiseaseCode();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);
  // Synchronous double-submit guard. `isPending` is React state that only
  // disables the button on the next render, so a fast double-click fires two
  // mutations (two error toasts) before the disabled prop applies — most
  // visible offline, where each request fails in milliseconds. A ref blocks
  // the second call immediately.
  const submittingRef = useRef(false);

  // Offline lock: a save can't complete without the network, so after an offline
  // attempt we surface the error and disable Save until the connection returns.
  const [offlineLocked, setOfflineLocked] = useState(false);
  useEffect(() => {
    const unlock = () => setOfflineLocked(false);
    window.addEventListener('online', unlock);
    return () => window.removeEventListener('online', unlock);
  }, []);

  useEffect(() => {
    if (!open) return;
    setForm(formFromCode(code));
    setErrors({});
    setIsDirty(false);
    setShowDiscard(false);
    setOfflineLocked(false);
  }, [open, code]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setIsDirty(true);
    if (errors[key as FieldKey]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key as FieldKey];
        return next;
      });
    }
  };

  const validate = (): {
    ok: boolean;
    code: string;
    name: string;
    description: string;
    status: DiseaseCodeStatus;
  } => {
    const next: Partial<Record<FieldKey, string>> = {};
    const cleanCode = form.code.trim().toUpperCase();
    const cleanName = form.name.trim();
    const cleanDescription = form.description.trim();
    // PRD 08 §8 — code required; format [A-Z][0-9]{2} (3자 고정, 소수점 불가); must
    // sit inside the owning group's ICD-10 range (lexicographic, uppercase).
    if (!cleanCode) {
      next.code = t('diseaseCodeManagement.errors.CODE_REQUIRED');
    } else if (!/^[A-Z][0-9]{2}$/.test(cleanCode)) {
      next.code = t('diseaseCodeManagement.errors.CODE_FORMAT_INVALID');
    } else if (rangeStart && rangeEnd && (cleanCode < rangeStart || cleanCode > rangeEnd)) {
      next.code = t('diseaseCodeManagement.errors.CODE_OUT_OF_RANGE');
    }
    // PRD 08 §4.1 — name required, 1–32 chars; charset = Hangul + Latin letter + digit + single space (DH-DISEASE-CHARSET-001).
    if (!cleanName) {
      next.name = t('diseaseCodeManagement.errors.CODE_REQUIRED');
    } else if (cleanName.length > 32) {
      next.name = t('diseaseCodeManagement.errors.DISEASE_NAME_TOO_LONG');
    } else if (!NAME_CHARSET_RE.test(cleanName)) {
      next.name = t('diseaseCodeManagement.errors.DISEASE_NAME_INVALID_CHAR');
    }
    // PRD 08 §4.1 — description optional, max 50 chars (특수문자·줄바꿈 허용).
    // SHCT-854 — "특수문자 허용" stops at 일반 특수문자: [공통] 00 §4.2 bans 이모지 for
    // every class, and the name field is already emoji-proof via NAME_CHARSET_RE,
    // so the description was the only way an emoji reached this screen's payload.
    if (cleanDescription.length > 50) {
      next.description = t('diseaseCodeManagement.errors.DISEASE_CODE_DESCRIPTION_TOO_LONG');
    } else if (hasEmoji(cleanDescription)) {
      next.description = t('common:errors.emojiNotAllowed');
    }
    setErrors(next);
    return {
      ok: Object.keys(next).length === 0,
      code: cleanCode,
      name: cleanName,
      // DC-3 — send the trimmed string ('' when cleared) so the BE clears the
      // description instead of ignoring a null. BE normalizes '' → null on save.
      description: cleanDescription,
      status: form.status,
    };
  };

  // Per CLAUDE.md §7.1, validation / form-context business-logic errors
  // (e.g. CODE_DUPLICATE, CODE_REQUIRED) show inline only — no toast. The toast
  // is reserved for system errors that carry no field (NETWORK, UNKNOWN, ...).
  const applyApiError = (e: unknown) => {
    const localized = getDiseaseCodeErrorMessage(
      e,
      t,
      'diseaseCodeManagement.toasts.saveError',
    );
    let inlineShown = false;
    if (e instanceof DiseaseCodeApiError && e.fields) {
      const next: Partial<Record<FieldKey, string>> = {};
      for (const k of Object.keys(e.fields)) {
        if (k === 'code' || k === 'name' || k === 'description' || k === 'status') {
          next[k as FieldKey] = localized;
        }
      }
      if (Object.keys(next).length > 0) {
        setErrors((prev) => ({ ...prev, ...next }));
        inlineShown = true;
      }
    }
    if (!inlineShown) {
      toast.error(localized);
    }
  };

  const handleSubmit = async () => {
    if (submittingRef.current || isPending) return;
    const v = validate();
    if (!v.ok) return;

    // Offline: don't fire a request that would hang the panel. Show the network
    // error and lock Save until we're back online (the 'online' listener unlocks).
    if (!navigator.onLine) {
      toast.error(t('diseaseCodeManagement.errors.NETWORK'));
      setOfflineLocked(true);
      return;
    }

    submittingRef.current = true;
    try {
      if (isEdit && code) {
        await updateMutation.mutateAsync({
          id: code.id,
          groupId,
          input: {
            code: v.code,
            name: v.name,
            description: v.description,
            status: v.status,
          },
        });
        toast.success(t('diseaseCodeManagement.toasts.codeUpdated'));
      } else {
        await createMutation.mutateAsync({
          groupId,
          input: {
            code: v.code,
            name: v.name,
            description: v.description,
            status: v.status,
          },
        });
        toast.success(t('diseaseCodeManagement.toasts.codeAdded'));
      }
      onClose();
    } catch (e) {
      applyApiError(e);
      // Connection dropped mid-request (offline flag didn't catch it): lock Save
      // until back online, matching the offline pre-check above.
      if (e instanceof DiseaseCodeApiError && e.code === 'NETWORK') {
        setOfflineLocked(true);
      }
    } finally {
      submittingRef.current = false;
    }
  };

  const handleClose = () => {
    if (isPending) return;
    if (isDirty) {
      setShowDiscard(true);
      return;
    }
    onClose();
  };

  const handleDiscardConfirm = () => {
    setShowDiscard(false);
    onClose();
  };

  return (
    <>
      <SlidePanel
        open={open}
        width={520}
        title={t(
          isEdit
            ? 'diseaseCodeManagement.codeForm.titleEdit'
            : 'diseaseCodeManagement.codeForm.titleAdd',
        )}
        onClose={handleClose}
      >
        <div className="form-field">
          <label className="form-field__label" htmlFor="dcCodeValue">
            {t('diseaseCodeManagement.codeForm.fields.code.label')}{' '}
            <span className="required">*</span>
          </label>
          <input
            id="dcCodeValue"
            type="text"
            className="form-field__input"
            value={form.code}
            onChange={(e) => update('code', e.target.value)}
            placeholder={t('diseaseCodeManagement.codeForm.fields.code.placeholder')}
          />
          {errors.code ? <p className="form-field__error">{errors.code}</p> : null}
        </div>

        <div className="form-field">
          <label className="form-field__label" htmlFor="dcCodeName">
            {t('diseaseCodeManagement.codeForm.fields.name.label')}{' '}
            <span className="required">*</span>
          </label>
          <input
            id="dcCodeName"
            type="text"
            className="form-field__input"
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            placeholder={t('diseaseCodeManagement.codeForm.fields.name.placeholder')}
          />
          {errors.name ? <p className="form-field__error">{errors.name}</p> : null}
        </div>

        <div className="form-field">
          <label className="form-field__label" htmlFor="dcCodeDesc">
            {t('diseaseCodeManagement.codeForm.fields.description.label')}
          </label>
          <textarea
            id="dcCodeDesc"
            className="form-field__textarea"
            rows={4}
            value={form.description}
            onChange={(e) => update('description', e.target.value)}
            placeholder={t('diseaseCodeManagement.codeForm.fields.description.placeholder')}
          />
          {errors.description ? (
            <p className="form-field__error">{errors.description}</p>
          ) : null}
        </div>

        <div className="form-field">
          <label className="form-field__label" htmlFor="dcCodeStatus">
            {t('diseaseCodeManagement.codeForm.fields.status.label')}{' '}
            <span className="required">*</span>
          </label>
          <select
            id="dcCodeStatus"
            className="form-field__select"
            value={form.status}
            onChange={(e) => update('status', e.target.value as DiseaseCodeStatus)}
          >
            <option value="ACTIVE">{t('diseaseCodeManagement.statuses.ACTIVE')}</option>
            <option value="INACTIVE">{t('diseaseCodeManagement.statuses.INACTIVE')}</option>
          </select>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 24 }}>
          <button
            type="button"
            className="btn btn-default btn-secondary"
            onClick={handleClose}
            disabled={isPending}
          >
            {t('diseaseCodeManagement.codeForm.actions.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-default btn-primary"
            onClick={handleSubmit}
            disabled={isPending || offlineLocked}
          >
            {t('diseaseCodeManagement.codeForm.actions.save')}
          </button>
        </div>
      </SlidePanel>

      <ConfirmModal
        open={showDiscard}
        title={t('diseaseCodeManagement.modals.discard.title')}
        message={t('diseaseCodeManagement.modals.discard.message')}
        confirmLabel={t('diseaseCodeManagement.modals.discard.confirm')}
        cancelLabel={t('diseaseCodeManagement.modals.discard.cancel')}
        confirmVariant="destructive"
        onConfirm={handleDiscardConfirm}
        onCancel={() => setShowDiscard(false)}
      />
    </>
  );
}
